import { type ReactNode } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { journalsQueries } from "@/entities/journal";
import { useCoordinator, useLocalUser } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import type { components } from "@entities/journal";
import { $journalApi } from "@entities/journal/fetch/fetch-client.ts";
import { PendingSign } from "./ui/pending-sign";
import { RejectedSign } from "./ui/RejectedSign";
import { SuccessSign } from "./ui/success-sign";
import "./representatives-signature-wrapper.scss";

type Representative =
  | components["schemas"]["Representative"]
  | components["schemas"]["ConstructionControl"];

type Signer = components["schemas"]["SignerInfo"];

type Developer = "developer";

type Representatives =
  | Developer
  | "buildingContractor"
  | "technicalCustomer"
  | "designerSupervisions"
  | "otherDevelopers";

export const RepresentativesSignatureWrapper = <T extends Representatives>({
  children,
  representative,
  isConstructionControl,
  representativeType,
  developerType,
}: {
  children: ReactNode;
  representative: Representative;
  isConstructionControl?: boolean;
  representativeType: T;
  developerType?: T extends Developer ? string : never;
}) => {
  const { titleId } = useCoordinator();
  const localUser = useLocalUser();

  const { data: titleJournal } = useSuspenseQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { data: titleSigners, refetch } = $journalApi.useQuery(
    "get",
    "/api/v1/journals/title/{titleId}/signs",
    {
      params: {
        path: { titleId },
      },
    },
  );

  if (!representative) return children;

  const signer = titleSigners?.signers?.find((signer: Signer) => {
    if (!isConstructionControl && "representativeId" in representative) {
      return signer.representativeId === representative.representativeId;
    }
    if (isConstructionControl && "constructionControlId" in representative) {
      return (
        signer.constructionControlId === representative.constructionControlId
      );
    }
    return false;
  });

  const showSignatureInfo = ["AT_SIGNING", "SIGNED", "SENT_TO_GSN", "REJECTED"];

  if (
    showSignatureInfo.includes(
      titleJournal?.mainJournal?.titleJournal?.status || "",
    )
  ) {
    if (signer?.status === "DONE")
      return (
        <SuccessSign signer={signer} mchdId={representative.mchdId}>
          {children}
        </SuccessSign>
      );

    if (
      signer?.status === "REJECTED" 
      // && signer.userInfo?.userId === localUser?.user_id
    )
      return <RejectedSign signer={signer}>{children}</RejectedSign>;

    if (
      signer?.status === "PENDING" 
      && signer.userInfo?.userId === localUser?.user_id &&
      titleJournal.mainJournal?.titleJournal?.status !== "REJECTED"
    ) {
      return (
        <PendingSign
          onSuccess={() => {
            refetch();
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
            });
          }}
          signer={signer}
          representativeType={representativeType}
          developerType={developerType}
        >
          {children}
        </PendingSign>
      );
    }

    return (
      <>
        {children}
      </>
    );
  }

  return <>{children}</>;
};
